import React, { useState, useEffect } from 'react';
import { X, Download } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { setupPWAInstall, promptPWAInstall, isPWA } from '../../utils/pwa';

/**
 * Install PWA Prompt Component
 * Shows banner prompting users to install the app
 */
const InstallPWA = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    // Don't show if already installed as PWA
    if (isPWA()) {
      return;
    }

    // Check if user has dismissed the prompt before
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    const dismissedDate = dismissed ? new Date(dismissed) : null;
    const daysSinceDismissed = dismissedDate 
      ? (new Date() - dismissedDate) / (1000 * 60 * 60 * 24) 
      : 999;

    // Show again after 7 days
    if (daysSinceDismissed > 7) {
      setupPWAInstall((canInstall) => {
        setShowPrompt(canInstall);
      });
    }
  }, []);

  const handleInstall = async () => {
    const accepted = await promptPWAInstall();
    if (accepted) {
      setShowPrompt(false);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-install-dismissed', new Date().toISOString());
  };

  if (!showPrompt) {
    return null;
  }

  return (
    <div className="fixed bottom-20 left-4 right-4 z-40 md:left-auto md:right-4 md:bottom-4 md:w-96">
      <Card className="bg-white shadow-xl border-edu-blue/20">
        <div className="p-4">
          <button
            onClick={handleDismiss}
            className="absolute top-2 right-2 p-1 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4 text-gray-500" />
          </button>
          
          <div className="flex items-start gap-3 mb-4">
            <div className="w-12 h-12 bg-edu-blue/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <Download className="h-6 w-6 text-edu-blue" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-sm mb-1">Install Guruskool</h3>
              <p className="text-xs text-gray-600">
                Install our app for faster access, offline support, and a better experience!
              </p>
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              size="sm"
              className="flex-1 bg-edu-blue hover:bg-edu-blue-dark text-white"
              onClick={handleInstall}
            >
              Install App
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleDismiss}
            >
              Not Now
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default InstallPWA;
